import { supabase } from './supabase';
import { loadStripe } from '@stripe/stripe-js';

const APPLE_PAY_MERCHANT_ID = 'merchant.07am.app';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const validateMerchant = async (validationURL: string) => {
  try {
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');

    // Call our backend validation endpoint
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-merchant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({ validationURL })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Validation response:', response.status, errorData);
      throw new Error(errorData.error || 'Merchant validation failed');
    }

    const { merchantSession } = await response.json();
    if (!merchantSession) {
      throw new Error('Invalid merchant session response');
    }

    return merchantSession;
  } catch (err) {
    console.error('Merchant validation error:', err);
    throw err;
  }
};

export async function initializeApplePay(amount: number): Promise<ApplePaySession | null> {
  // Check if Apple Pay is supported by the browser
  if (!window.ApplePaySession) {
    console.log('Apple Pay is not supported by the browser');
    return null;
  }

  // Check if Apple Pay is available
  if (!ApplePaySession.canMakePayments()) {
    console.log('Apple Pay is not available');
    return null;
  }

  // Check if user has an active card
  const canMakePayments = await ApplePaySession.canMakePaymentsWithActiveCard(APPLE_PAY_MERCHANT_ID);
  if (!canMakePayments) {
    console.log('No active card available for Apple Pay');
    return null;
  }

  if (!amount || amount <= 0) {
    throw new Error('Invalid payment amount');
  }

  // If we get here, Apple Pay is fully available
  try {
    const paymentRequest = {
      countryCode: 'US',
      currencyCode: 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex'],
      merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
      total: {
        label: '07:AM Premium Subscription',
        amount: amount.toFixed(2),
      },
      merchantIdentifier: APPLE_PAY_MERCHANT_ID
    };

    return new ApplePaySession(3, paymentRequest);
  } catch (err) {
    console.error('Error initializing Apple Pay session:', err);
    throw new Error('Failed to initialize Apple Pay session');
  }
}

export async function processApplePayment(
  session: ApplePaySession, 
  onSuccess: () => void,
  onError: (error: string) => void
) {
  try {
    session.onvalidatemerchant = async (event) => {
      try {
        console.log('Validating merchant...');
        const merchantSession = await validateMerchant(event.validationURL);
        session.completeMerchantValidation(merchantSession);
      } catch (err) {
        console.error('Merchant validation failed:', err);
        session.abort();
        onError('Payment validation failed');
      }
    };

    session.onpaymentauthorized = async (event) => {
      try {
        console.log('Processing payment...');
        // Process payment
        const { token } = event.payment;
        
        // Send token to your payment processor
        const { data: { session: authSession } } = await supabase.auth.getSession();
        if (!authSession) throw new Error('Not authenticated');

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession.access_token}`
          },
          body: JSON.stringify({
            payment: token,
            amount: event.payment.total.amount
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Payment processing failed');
        }

        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        onSuccess();
      } catch (err) {
        console.error('Payment processing failed:', err);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        onError(err instanceof Error ? err.message : 'Payment processing failed');
      }
    };

    session.oncancel = () => {
      console.log('Payment cancelled by user');
      onError('Payment cancelled');
    };

    session.onshippingcontactselected = (event) => {
      session.completeShippingContactSelection(ApplePaySession.STATUS_SUCCESS, [], {
        label: '07:AM Premium Subscription',
        amount: event.payment.total.amount
      }, []);
    };

    console.log('Starting Apple Pay session...');
    session.begin();
  } catch (err) {
    console.error('Apple Pay error:', err);
    onError('Payment failed to initialize');
  }
}

export async function initializeStripePayment(amount: number): Promise<string | null> {
  try {
    const stripe = await stripePromise;

    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Convert amount to cents
    const amountInCents = Math.round(amount * 100);

    // Create payment intent
    // Validate amount
    if (!amount || amount <= 0) {
      throw new Error('Invalid payment amount');
    }

    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ 
        successUrl: `${window.location.origin}/profile?payment=success`,
        cancelUrl: `${window.location.origin}/subscription?payment=canceled`
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Payment setup error:', response.status, errorData);
      throw new Error(errorData.error || 'Unable to process payment. Please try again.');
    }

    const { clientSecret } = await response.json();
    if (!clientSecret) throw new Error('No client secret returned');

    return clientSecret;
  } catch (err) {
    throw err;
  }
}

export async function processStripePayment(
  clientSecret: string,
  onSuccess: () => void,
  onError: (error: string) => void
) {
  try {
    const stripe = await stripePromise;
    if (!stripe) throw new Error('Stripe failed to initialize');

    const { error } = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      }
    });

    if (error) throw error;
    onSuccess();
  } catch (err) {
    console.error('Stripe payment error:', err);
    onError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
  }
}